import { getDaysOfTheWeek } from '@utils/date/daysOfTheWeek';
import { isIntegrationUnavailable } from '@utils/integrationErrorCode';
import { SpaEntityWithTimeslots } from 'shared-types/Spa';

/**
 * A week is one request per day, so a failed week has several errors and the caller only ever sees
 * one of them. Promise.all hands over whichever rejected first, and a fast failure of any other
 * kind beats an integration timeout — which reads to the guest as "no availability" during the
 * outage this contract exists to describe. So the unreachable one wins when it is present.
 *
 * Still all or nothing: a partial week is not an answer about a week.
 */
export async function getWeeklyTimeslots(
  getTimeslotsFunction: (arrivalDate: string) => Promise<SpaEntityWithTimeslots>,
  startDate: string,
  numberOfDays = 7
) {
  const days = getDaysOfTheWeek(startDate, numberOfDays);
  const settled = await Promise.allSettled(days.map((day) => getTimeslotsFunction(day)));

  const reasons = settled
    .filter((result): result is PromiseRejectedResult => result.status === 'rejected')
    .map((result) => result.reason);

  if (reasons.length > 0) {
    throw reasons.find(isIntegrationUnavailable) ?? reasons[0];
  }

  return settled
    .filter(
      (result): result is PromiseFulfilledResult<SpaEntityWithTimeslots> =>
        result.status === 'fulfilled'
    )
    .map((result) => result.value.availability)
    .flat(1);
}
